import { useEffect, useState } from "react";
import PageTitle from "components/common/Typography/PageTitle";
import { API } from "constants/RestApi";
import { IRole } from "core/types/base/IRole";
import { useBaseAction, useHandleModals, useUserRole } from "hooks/core";
import { toast } from "react-toastify";
import DataTable from "components/common/DataTable/DataTable";
import RolesTableColumns from "constants/tables/rolesTableColumns";
import CreateEditRoleModal from "components/common/Modals/CreateEditRoleModal";
import WarningModal from "components/common/Modals/WarningModal";
import { UserPermissions } from "../../../../core/types/enums/PermissionsEnum";
import "./RoleManagementScreen.scss";

const RoleManagementScreen = () => {
  const { request } = useBaseAction();
  const [roles, setRoles] = useState<IRole[]>([]);
  const actions = ["addEdit", "delete"] as const;
  const { toggleModal, modals, selectedEntity } = useHandleModals<IRole, typeof actions[number]>({
    modalsKeys: actions,
  });
  const hideModal = () => toggleModal({ hideAll: true });
  const { permissionGranted } = useUserRole();
  const canUpsert = permissionGranted([UserPermissions["Role.Upsert"]]);
  const canRemove = permissionGranted([UserPermissions["Role.Remove"]]);

  const getRoles = async () => {
    try {
      const response = await request({
        method: "GET",
        url: `${API.ADMIN_ROLES_WITH_PERMISSIONS}`,
        useLoader: true,
      });
      setRoles(response);
    } catch (error: any) {
      toast.error(error?.message || "Failed to fetch roles");
    }
  };

  const createEditRole = async (role: IRole, isEditMode: boolean, callback?: () => void) => {
    const body = {
      name: role.name,
      tenantId: role.tenantId,
      permissions: role.permissions || [],
    };
    const updateBody = {
      ...body,
      id: role.id,
    };

    // Create or update role with permissions in one request
    await request({
      method: isEditMode ? "PUT" : "POST",
      url: API.ADMIN_ROLES_WITH_PERMISSIONS,
      useLoader: true,
      body: isEditMode ? updateBody : body,
      successMessage: isEditMode ? "Role has been updated" : "Role has been created",
      errorMessage: "Something went wrong!",
    });

    await getRoles();
    if (callback) callback();
    toggleModal({ hideAll: true });
  };

  const deleteRole = async () => {
    const url = API.ADMIN_ROLE.replace("{id}", selectedEntity?.id || "");
    try {
      await request({
        method: "DELETE",
        url,
        useLoader: true,
        successMessage: "Role deleted successfully",
        // Don't provide errorMessage to let useBaseAction handle it automatically
      });
      await getRoles();
      toggleModal({ key: "warning", value: true, hideAll: true });
    } catch (error: any) {
      // Error is already handled by useBaseAction, no need to show additional toast
      console.error("Error deleting role:", error);
    }
  };

  const columns = RolesTableColumns({
    onEdit: (role) => {
      toggleModal({
        key: "addEdit",
        value: true,
        selectedItem: role,
      });
    },
    onDelete: (id) => {
      toggleModal({
        key: "delete",
        value: true,
        selectedItem: {
          id,
        } as IRole,
      });
    },
    canUpsert,
    canRemove,
  });

  useEffect(() => {
    getRoles();
  }, []);

  return (
    <>
      <PageTitle
        pageTitle="Manage Roles"
        btnCreateText="Add role"
        onBtnClicked={
          canUpsert
            ? () => {
                toggleModal({ key: "addEdit", value: true });
              }
            : undefined
        }
      />
      <div className="col-12 mx-auto mb-5">
        <div className="card shadow-lg">
          <DataTable
            tableData={roles}
            tableColumns={columns}
            tableClassName="table-striped mb-0"
            classContainer="table-responsive roles-table-container"
          />
        </div>
      </div>
      <CreateEditRoleModal
        selectedEntity={selectedEntity || ({} as IRole)}
        showModal={modals.addEdit}
        onCancel={hideModal}
        onClose={hideModal}
        onSave={(modalData, isEditMode, callback) =>
          createEditRole(modalData!, isEditMode!, callback)
        }
      />
      <WarningModal
        showModal={modals.delete}
        onCancel={hideModal}
        onClose={hideModal}
        modalTitle="Are you sure you want to delete this role ?"
        onSave={deleteRole}
      />
    </>
  );
};

export default RoleManagementScreen;
